// MembershipsManager.jsx - Main memberships admin page
// 3 tabs: Plans / Active Members / Charges

import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "./supabase";
import MembershipDetail from "./MembershipDetail";
import { fnErrorMessage } from "./fnError";

const STRIPE_PUBLISHABLE_KEY = "pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh";
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export default function MembershipsManager() {
  const [tab, setTab] = useState("plans");
  const [detailMembershipId, setDetailMembershipId] = useState(null);

  if (detailMembershipId) {
    return <MembershipDetail membershipId={detailMembershipId} onBack={() => setDetailMembershipId(null)} />;
  }

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>⭐ Memberships</div>
        <div style={{fontSize:12,color:C.muted}}>Manage subscription plans, active members, and billing history</div>
      </div>

      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:"1px solid "+C.border,width:"fit-content",marginBottom:18}}>
        {[
          ["plans", "📋 Plans"],
          ["members", "👥 Active Members"],
          ["charges", "💳 Charges"],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} style={{
            padding:"7px 16px",fontSize:12,fontWeight:tab===v?700:500,
            color:tab===v?"#fff":C.muted,
            background:tab===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"none",
            borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,
          }}>{l}</button>
        ))}
      </div>

      {tab === "plans" && <PlansTab />}
      {tab === "members" && <MembersTab onView={(id) => setDetailMembershipId(id)} />}
      {tab === "charges" && <ChargesTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PLANS TAB
// ════════════════════════════════════════════════════════════
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("membership_plans").select("*").order("display_order").order("id");
    setPlans(data || []);
    setLoading(false);
  };

  return (
    <div>
      <button onClick={() => setEditing("new")} style={{
        marginBottom:14,padding:"10px 18px",
        background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",
        color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,
        cursor:"pointer",fontFamily:F,
      }}>+ Create New Plan</button>

      {loading ? (
        <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>
      ) : plans.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
          <div style={{fontSize:36,marginBottom:8}}>⭐</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>No membership plans yet</div>
          <div style={{fontSize:12,color:C.muted}}>Create your first plan to start enrolling members</div>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
          {plans.map(p => <PlanCard key={p.id} plan={p} onEdit={() => setEditing(p)} onChanged={load} />)}
        </div>
      )}

      {editing && <PlanEditor plan={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PlanCard({ plan, onEdit, onChanged }) {
  const [activeCount, setActiveCount] = useState(0);

  useEffect(() => {
    (async () => {
      const { count } = await supabase.from("client_memberships")
        .select("*", { count: "exact", head: true })
        .eq("plan_id", plan.id)
        .eq("status", "active");
      setActiveCount(count || 0);
    })();
  }, [plan.id]);

  const intervalText = plan.billing_interval_count > 1 
    ? `every ${plan.billing_interval_count} ${plan.billing_interval}s`
    : `per ${plan.billing_interval}`;

  return (
    <div style={{
      background:C.card,
      border:"1px solid "+C.border,
      borderTop:"4px solid "+(plan.color || C.gold),
      borderRadius:12,
      padding:18,
      position:"relative",
      opacity: plan.is_active ? 1 : 0.5,
    }}>
      {plan.badge_text && (
        <div style={{
          position:"absolute",top:-10,right:14,
          background:"linear-gradient(135deg,"+(plan.color||C.gold)+","+C.goldD+")",
          color:"#fff",padding:"3px 10px",borderRadius:50,fontSize:10,fontWeight:700,
          textTransform:"uppercase",letterSpacing:"0.05em",
        }}>{plan.badge_text}</div>
      )}

      <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S,marginBottom:6}}>{plan.name}</div>
      
      <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:8}}>
        <span style={{fontSize:28,fontWeight:800,color:plan.color||C.gold,fontFamily:S}}>
          ${(+plan.price).toFixed(0)}
        </span>
        <span style={{fontSize:11,color:C.muted}}>{intervalText}</span>
      </div>

      {plan.description && <div style={{fontSize:11,color:C.muted,lineHeight:1.5,marginBottom:10}}>{plan.description}</div>}

      {(plan.features || []).length > 0 && (
        <div style={{marginBottom:12,paddingTop:10,borderTop:"1px solid "+C.border}}>
          {(plan.features || []).slice(0, 4).map((f, i) => (
            <div key={i} style={{fontSize:11,color:C.text,marginBottom:4,display:"flex",gap:6}}>
              <span style={{color:C.green}}>✓</span>{f}
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,borderTop:"1px solid "+C.border}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:C.gold}}>{activeCount}</div>
          <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em"}}>Active</div>
        </div>
        <button onClick={onEdit} style={{padding:"6px 14px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>
          Edit
        </button>
      </div>

      {!plan.stripe_price_id && (
        <div style={{marginTop:8,padding:"6px 10px",background:"rgba(255,165,0,0.1)",border:"1px solid rgba(255,165,0,0.3)",borderRadius:6,fontSize:10,color:"#cc6600"}}>
          ⚠ Not synced to Stripe yet
        </div>
      )}
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan || {
    name: "",
    description: "",
    price: 99,
    billing_interval: "month",
    billing_interval_count: 1,
    features: [],
    color: "#b48c3c",
    badge_text: "",
    is_active: true,
    display_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [featureInput, setFeatureInput] = useState("");

  const update = (k, v) => setForm(f => ({...f, [k]: v}));

  const addFeature = () => {
    if (!featureInput.trim()) return;
    update("features", [...(form.features || []), featureInput.trim()]);
    setFeatureInput("");
  };

  const removeFeature = (i) => {
    update("features", (form.features || []).filter((_, idx) => idx !== i));
  };

  const save = async () => {
    if (!form.name?.trim()) { setError("Name required"); return; }
    if (!form.price || +form.price <= 0) { setError("Price must be > 0"); return; }

    setSaving(true);
    setError("");

    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      price: +form.price,
      billing_interval: form.billing_interval,
      billing_interval_count: +form.billing_interval_count || 1,
      features: form.features || [],
      color: form.color || "#b48c3c",
      badge_text: form.badge_text?.trim() || null,
      is_active: form.is_active !== false,
      display_order: +form.display_order || 0,
      updated_at: new Date().toISOString(),
    };

    let planId;
    if (plan?.id) {
      const { error: e } = await supabase.from("membership_plans").update(payload).eq("id", plan.id);
      if (e) { setError(e.message); setSaving(false); return; }
      planId = plan.id;
    } else {
      const { data, error: e } = await supabase.from("membership_plans").insert(payload).select().single();
      if (e) { setError(e.message); setSaving(false); return; }
      planId = data.id;
    }

    // Sync to Stripe
    try {
      const action = plan?.id ? "update_plan" : "create_plan";
      const { data: result, error: fnErr } = await supabase.functions.invoke("stripe-membership", {
        body: { action, plan_id: planId },
      });
      if (fnErr || result?.error) throw new Error(await fnErrorMessage(fnErr, result, "Stripe sync failed"));
    } catch (e) {
      setError("Plan saved but Stripe sync failed: " + (e.message || e) + " — you can retry by editing again");
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:14,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
          <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S}}>{plan ? "Edit Plan" : "New Membership Plan"}</div>
          <div style={{fontSize:11,color:C.muted}}>Stripe Product + Price will be {plan ? "updated" : "auto-created"}</div>
        </div>

        <div style={{padding:22}}>
          <Field label="Plan Name *">
            <input value={form.name} onChange={e=>update("name", e.target.value)} style={inp} placeholder="Glow Club VIP"/>
          </Field>

          <Field label="Description">
            <textarea value={form.description||""} onChange={e=>update("description", e.target.value)} rows={2} style={{...inp, resize:"vertical"}} placeholder="Premium membership with monthly facials and exclusive perks"/>
          </Field>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            <Field label="Price ($) *">
              <input type="number" step="0.01" value={form.price} onChange={e=>update("price", e.target.value)} style={inp}/>
            </Field>
            <Field label="Billing Cycle">
              <select value={form.billing_interval} onChange={e=>update("billing_interval", e.target.value)} style={inp}>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </Field>
            <Field label="Every N">
              <input type="number" min="1" value={form.billing_interval_count} onChange={e=>update("billing_interval_count", e.target.value)} style={inp} placeholder="1"/>
            </Field>
          </div>

          <Field label={`What's Included (${(form.features||[]).length} items)`}>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <input value={featureInput} onChange={e=>setFeatureInput(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addFeature(); }}} style={{...inp, flex:1}} placeholder="e.g. 1 free facial per month"/>
              <button onClick={addFeature} style={{padding:"7px 14px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>+ Add</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {(form.features || []).map((f, i) => (
                <div key={i} style={{display:"flex",gap:6,alignItems:"center",padding:"6px 10px",background:C.greenL,borderRadius:6}}>
                  <span style={{color:C.green}}>✓</span>
                  <span style={{flex:1,fontSize:12,color:C.text}}>{f}</span>
                  <button onClick={()=>removeFeature(i)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:14}}>×</button>
                </div>
              ))}
            </div>
          </Field>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Brand Color">
              <input type="color" value={form.color} onChange={e=>update("color", e.target.value)} style={{width:"100%",height:38,border:"1px solid "+C.border,borderRadius:7,cursor:"pointer",padding:2}}/>
            </Field>
            <Field label='Badge (e.g. "Most Popular")'>
              <input value={form.badge_text||""} onChange={e=>update("badge_text", e.target.value)} style={inp} placeholder="optional"/>
            </Field>
          </div>

          <Field label="Active">
            <button onClick={()=>update("is_active", !form.is_active)} style={{padding:"7px 14px",background:form.is_active?"linear-gradient(135deg,"+C.green+",#1e6b3f)":C.light,color:form.is_active?"#fff":C.muted,border:"1px solid "+(form.is_active?C.green:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>
              {form.is_active ? "✓ Active" : "Inactive"}
            </button>
          </Field>

          {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:12,marginTop:10}}>❌ {error}</div>}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16,paddingTop:14,borderTop:"1px solid "+C.border}}>
            <button onClick={onClose} disabled={saving} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={save} disabled={saving} style={{padding:"9px 22px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1}}>
              {saving?"Saving + syncing to Stripe...":(plan?"Save Changes":"Create Plan")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MEMBERS TAB
// ════════════════════════════════════════════════════════════
function MembersTab({ onView }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollPreset, setEnrollPreset] = useState(null); // set when converting a POS member to auto-billing
  const [cancelingId, setCancelingId] = useState(null);

  useEffect(() => { load(); }, [statusFilter]);

  // Cancel a subscription membership → stops monthly billing (at period end).
  const cancelMembership = async (m) => {
    if (!window.confirm(`Cancel ${m.clients?.name || "this member"}'s membership?\n\nBilling will STOP — they won't be charged again.`)) return;
    setCancelingId(m.id);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "cancel_subscription", membership_id: m.id, immediate: false },
      });
      if (error || data?.error) throw new Error(await fnErrorMessage(error, data, "Cancel failed"));
      await load();
      alert("✅ Membership cancelled — no more monthly charges.");
    } catch (e) {
      alert("Could not cancel: " + (e.message || e));
    } finally {
      setCancelingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    // 1) Subscription members (Stripe auto-billing). "Active only" must also
    //    include trialing — a migrated member on a billing-day anchor is active.
    let q = supabase.from("client_memberships")
      .select("*, clients(id,name,email,phone), membership_plans(name,price,billing_interval,color)")
      .order("created_at", { ascending: false });
    if (statusFilter === "active") q = q.in("status", ["active", "trialing"]);
    else if (statusFilter !== "all") q = q.eq("status", statusFilter);
    const { data: subs } = await q;
    let list = (subs || []).map(m => ({ ...m, _source: "sub" }));

    // 2) POS-sold memberships — charged at the register (cash/card), not as a
    //    Stripe subscription, so they never land in client_memberships. Surface
    //    them here so the owner sees ALL members in one place. Only on the
    //    active/all views (a one-time POS membership has no paused/cancelled).
    if (statusFilter === "active" || statusFilter === "all") {
      const { data: plans } = await supabase.from("membership_plans").select("id,name,price,billing_interval,color");
      const planList = plans || [];
      const { data: pays } = await supabase.from("payments")
        .select("id,client_id,client_name,amount,description,created_at,status")
        .ilike("description", "%membership%")
        .order("created_at", { ascending: false })
        .limit(500);
      const seen = new Set(list.map(m => m.client_id));   // skip clients who already have a subscription
      const byClient = new Map();                          // latest membership payment per client
      for (const p of (pays || [])) {
        if (!p.client_id || Number(p.amount) <= 0) continue;
        if (["voided", "refunded"].includes(p.status)) continue;
        if (seen.has(p.client_id) || byClient.has(p.client_id)) continue;
        const plan = planList.find(pl => (p.description || "").toLowerCase().includes(pl.name.toLowerCase()));
        byClient.set(p.client_id, {
          id: "pos-" + p.id,
          _source: "pos",
          client_id: p.client_id,
          plan_id: plan?.id || null,
          clients: { id: p.client_id, name: p.client_name },
          membership_plans: plan || { name: "Membership (POS)", price: p.amount, billing_interval: "one-time", color: C.purple },
          status: "active",
          started_at: p.created_at,
          next_billing_at: null,
        });
      }
      list = [...list, ...byClient.values()];
    }
    setMembers(list);
    setLoading(false);
  };

  const filtered = members.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.clients?.name?.toLowerCase().includes(q) 
      || m.clients?.email?.toLowerCase().includes(q)
      || m.membership_plans?.name?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search by client name, email, or plan..." style={{...inp, flex:1, minWidth:240}}/>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{...inp, width:"auto"}}>
          <option value="active">Active only</option>
          <option value="incomplete">Pending</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="past_due">Past due</option>
          <option value="all">All statuses</option>
        </select>
        <button onClick={()=>setEnrolling(true)} style={{padding:"9px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>
          + Enroll Client
        </button>
      </div>

      {loading ? (
        <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
          <div style={{fontSize:36,marginBottom:8}}>👥</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>No members in this view</div>
          <div style={{fontSize:12,color:C.muted}}>Sell a membership at checkout, or click "Enroll Client" for auto-billing.</div>
        </div>
      ) : (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
          {filtered.map(m => (
            <div key={m.id} onClick={() => m._source === "sub" && onView(m.id)} style={{
              padding:"14px 18px",borderBottom:"1px solid "+C.border,
              display:"flex",alignItems:"center",gap:14,cursor: m._source === "sub" ? "pointer" : "default",
              transition:"background 0.15s",
            }}
            onMouseEnter={e=>{ if(m._source==="sub") e.currentTarget.style.background=C.goldBg; }}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{width:44,height:44,borderRadius:"50%",background:m.membership_plans?.color||C.gold,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:16,fontWeight:700,flexShrink:0}}>
                {m.clients?.name?.[0] || "?"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{m.clients?.name || "Unknown"}</div>
                <div style={{fontSize:11,color:C.muted}}>{m.clients?.email || m.clients?.phone}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:12,fontWeight:600,color:m.membership_plans?.color||C.gold}}>
                  {m.membership_plans?.name}
                  {m._source === "pos" && <span style={{marginLeft:6,fontSize:9,fontWeight:700,color:C.purple,background:"rgba(124,58,237,0.12)",padding:"1px 6px",borderRadius:50,verticalAlign:"middle"}}>POS</span>}
                </div>
                <div style={{fontSize:11,color:C.muted}}>${(+m.membership_plans?.price||0).toFixed(0)}/{m.membership_plans?.billing_interval}</div>
              </div>
              <StatusBadge status={m.status}/>
              {m._source === "pos" ? (
                <button
                  onClick={(e)=>{ e.stopPropagation(); setEnrollPreset({ client: { id: m.client_id, name: m.clients?.name }, planId: m.plan_id || "", billingDay: m.started_at ? new Date(m.started_at).getDate() : "" }); }}
                  title="Turn this POS membership into auto-billing — charges the card monthly on this day-of-month"
                  style={{padding:"7px 12px",background:"linear-gradient(135deg,"+C.purple+",#5b21b6)",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap",flexShrink:0}}>
                  ⚡ Auto-bill
                </button>
              ) : (
                <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                  <div style={{textAlign:"right",fontSize:11,color:C.muted,minWidth:90}}>
                    <div style={{fontWeight:600}}>{m.cancelled_at && m.status!=="cancelled" ? "Cancelling" : "Next charge"}</div>
                    <div>{m.cancelled_at && m.status!=="cancelled" ? "at period end" : (m.next_billing_at ? new Date(m.next_billing_at).toLocaleDateString() : "—")}</div>
                  </div>
                  {m.status !== "cancelled" && !m.cancelled_at && m.stripe_subscription_id && (
                    <button
                      onClick={(e)=>{ e.stopPropagation(); cancelMembership(m); }}
                      disabled={cancelingId===m.id}
                      title="Cancel membership — stops monthly billing"
                      style={{padding:"7px 12px",background:"#fff",color:C.red,border:"1px solid "+C.red+"55",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap",flexShrink:0}}>
                      {cancelingId===m.id?"…":"✕ Cancel"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {enrolling && <EnrollClientModal onClose={()=>setEnrolling(false)} onEnrolled={()=>{setEnrolling(false); load();}}/>}
      {enrollPreset && <EnrollClientModal preset={enrollPreset} onClose={()=>setEnrollPreset(null)} onEnrolled={()=>{setEnrollPreset(null); load();}}/>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { bg: C.greenL, color: C.green, label: "Active" },
    paused: { bg: C.blueL, color: C.blue, label: "Paused" },
    cancelled: { bg: C.redL, color: C.red, label: "Cancelled" },
    past_due: { bg: "rgba(255,165,0,0.1)", color: "#cc6600", label: "Past Due" },
    incomplete: { bg: C.light, color: C.muted, label: "Pending" },
  };
  const s = map[status] || map.incomplete;
  return (
    <span style={{padding:"4px 10px",background:s.bg,color:s.color,borderRadius:50,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",flexShrink:0}}>
      {s.label}
    </span>
  );
}

// Inner card-collection form that runs inside the Stripe <Elements>
// context. Confirms the SetupIntent with the card the staff types in,
// then asks the backend to persist the resulting payment_method to
// saved_payment_methods so it's reusable for next month + future flows.
function SaveCardForm({ clientId, onSaved, onCancel, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements) return;
    setBusy(true);
    onError?.("");
    try {
      // Confirm the SetupIntent — attaches the card to the Stripe customer.
      const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {},
      });
      if (confirmErr) {
        onError?.(confirmErr.message || "Card validation failed");
        setBusy(false);
        return;
      }
      const pmId = setupIntent?.payment_method;
      if (!pmId) {
        onError?.("No payment method returned by Stripe");
        setBusy(false);
        return;
      }
      // Save to saved_payment_methods + set as default
      const { data, error: saveErr } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "save_payment_method", client_id: clientId, payment_method_id: pmId, set_default: true },
      });
      if (saveErr || data?.error) {
        onError?.(await fnErrorMessage(saveErr, data, "Could not save card"));
        setBusy(false);
        return;
      }
      onSaved?.(data.saved);
    } catch (e) {
      onError?.(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement options={{ layout: "tabs" }}/>
      <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
        <button type="button" onClick={onCancel} disabled={busy} style={{padding:"7px 14px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:11,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>Cancel</button>
        <button type="submit" disabled={busy || !stripe} style={{padding:"7px 18px",background:C.gold,color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F,opacity:busy?0.6:1}}>
          {busy ? "Saving card…" : "💾 Save Card"}
        </button>
      </div>
    </form>
  );
}

function EnrollClientModal({ onClose, onEnrolled, preset }) {
  const [plans, setPlans] = useState([]);
  // ★ preset pre-fills the form when converting an existing POS member to
  //   auto-billing: client + plan + their original billing day-of-month.
  const [clientId, setClientId] = useState(preset?.client?.id ? String(preset.client.id) : "");
  const [planId, setPlanId] = useState(preset?.planId ? String(preset.planId) : "");
  const [billingDay, setBillingDay] = useState(preset?.billingDay ? String(preset.billingDay) : ""); // 1-31; set when migrating an existing member
  const [enrollResult, setEnrollResult] = useState(null); // success/decline confirmation
  const [savedCards, setSavedCards] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // ★ Inline card-collection state — when there are no saved cards on
  // file we let staff add one without leaving the enroll flow.
  const [addingCard, setAddingCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);

  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickedClient, setPickedClient] = useState(preset?.client || null);

  // Load active plans once. Clients are searched on-demand server-side
  // (see the search effect below) — we used to bulk-load all 2,500+ clients
  // onto the device, which was unreliable on flaky mobile connections: if any
  // page of the load failed, clients late in the alphabet silently vanished
  // (owner couldn't find Safiyyah Karim / Saba Riaz). Server search is instant
  // and always finds anyone regardless of total count or connection quality.
  useEffect(() => {
    (async () => {
      const { data: p } = await supabase.from("membership_plans").select("*").eq("is_active", true).order("display_order");
      setPlans(p || []);
    })();
  }, []);

  // ★ Debounced server-side client search — by name, email, or phone.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    const esc = q.replace(/[%(),*]/g, " ").trim();
    if (!esc) { setResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id,name,email,phone")
        .or(`name.ilike.*${esc}*,email.ilike.*${esc}*,phone.ilike.*${esc}*`)
        .order("name")
        .limit(40);
      if (cancelled) return;
      setSearching(false);
      if (!error) setResults(data || []);
      else console.error("[Memberships] client search error:", error);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  useEffect(() => {
    if (!clientId) { setSavedCards([]); setPaymentMethodId(""); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("saved_payment_methods").select("*").eq("client_id", clientId);
      let cards = (data || []).map(c => ({
        stripe_payment_method_id: c.stripe_payment_method_id,
        card_brand: c.card_brand, card_last4: c.card_last4, is_default: c.is_default,
      }));
      // ★ Fallback: also pull cards attached at Stripe that aren't mirrored in
      // saved_payment_methods (e.g. a card saved through the POS/terminal, or a
      // DB write that failed after the Stripe attach succeeded — this is exactly
      // why Lauren Matarazzo's card-on-file wasn't selectable). Without this, a
      // real chargeable card is invisible here and staff can't enroll on it.
      try {
        const { data: live } = await supabase.functions.invoke("stripe-membership", {
          body: { action: "list_payment_methods", client_id: +clientId },
        });
        for (const m of (live?.methods || [])) {
          if (!cards.find(c => c.stripe_payment_method_id === m.id)) {
            cards.push({ stripe_payment_method_id: m.id, card_brand: m.brand, card_last4: m.last4, is_default: m.is_default });
          }
        }
      } catch (_) { /* offline / function error — fall back to DB cards only */ }
      if (cancelled) return;
      setSavedCards(cards);
      const def = cards.find(c => c.is_default) || cards[0];
      if (def) setPaymentMethodId(def.stripe_payment_method_id);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // (client search is server-side now — see the debounced search effect above)

  const enroll = async () => {
    if (!clientId) { setError("Pick a client"); return; }
    if (!planId) { setError("Pick a plan"); return; }

    setSaving(true);
    setError("");

    try {
      const { data, error: fnErr } = await supabase.functions.invoke("stripe-membership", {
        body: { 
          action: "subscribe_client",
          client_id: +clientId,
          plan_id: +planId,
          payment_method_id: paymentMethodId || null,
          billing_day: billingDay ? +billingDay : null,
        },
      });
      if (fnErr) {
        // supabase-js wraps a non-2xx as a generic "Edge Function returned a
        // non-2xx status code". Pull the real server error out of the body.
        // Clone first: the body can only be read once, and a failed read used
        // to leave staff staring at the generic message with no way forward.
        const real = await fnErrorMessage(fnErr, data, "Enrollment failed");
        console.error("[enroll] server error:", real, fnErr);
        throw new Error(real);
      }
      if (data?.error) throw new Error(data.error);

      // ★ Confirm the outcome out loud before closing — enrolling charges a
      //   real card, and staff were left guessing whether it went through.
      const planObj = plans.find(p => String(p.id) === String(planId));
      const amount = planObj ? `$${(+planObj.price).toFixed(0)}` : "";
      setEnrollResult(
        data?.deduped
          ? { ok: true, title: "Already enrolled", body: data.message || "This client already has an active membership on this plan — not charged again." }
          : billingDay
            ? { ok: true, title: "Enrolled — no charge today", body: `${amount} will be charged on day ${+billingDay} of the month, then monthly.` }
            : data?.status === "past_due" || data?.status === "incomplete"
              ? { ok: false, title: "Enrolled, but the card didn't go through", body: `Status: ${data.status}. Update the card and use "Charge Now" on the membership.` }
              : { ok: true, title: "Payment Successful", body: `${amount} charged now. Billing repeats automatically on this date every month.`, benefits: data?.benefits?.items || [] }
      );
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  // Stripe.js readiness — on a flaky connection js.stripe.com can fail to
  // load, which would leave the card form area blank. Track it so we can show
  // a clear message instead of an empty box.
  const [stripeReady, setStripeReady] = useState(null); // null = loading
  useEffect(() => {
    let alive = true;
    Promise.resolve(stripePromise)
      .then(s => { if (alive) setStripeReady(!!s); })
      .catch(() => { if (alive) setStripeReady(false); });
    return () => { alive = false; };
  }, []);

  // Shared "open the secure card form" action for both the no-card and the
  // "add another card" buttons. Hardened with a 20s timeout so a dropped
  // mobile connection surfaces a clear message instead of hanging on "Opening…".
  const startAddCard = async () => {
    if (!clientId) { setError("Pick a client first"); return; }
    setStripeLoading(true); setError("");
    try {
      // Direct fetch instead of supabase.functions.invoke: invoke was handing
      // back a response whose client_secret never surfaced in this browser
      // (the server returns it fine — verified). Parsing the JSON ourselves
      // removes that variable entirely. 20s abort guard for flaky connections.
      const { data: { session } } = await supabase.auth.getSession();
      const ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ1b2VqeWtncG5mcGhydnFqdXViIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY2MDUwOTcsImV4cCI6MjA5MjE4MTA5N30.-Iea55F7GszWyCny9Br94t6A9vpD8oCyQPfyw1gE8fw";
      const token = session?.access_token || ANON;
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 20000);
      let resp, text;
      try {
        resp = await fetch("https://fuoejykgpnfphrvqjuub.supabase.co/functions/v1/stripe-membership", {
          method: "POST",
          headers: { "Content-Type": "application/json", apikey: ANON, Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: "create_setup_intent", client_id: +clientId }),
          signal: ctrl.signal,
        });
        text = await resp.text();
      } finally { clearTimeout(tid); }
      let parsed = {};
      try { parsed = text ? JSON.parse(text) : {}; } catch (_) { parsed = {}; }
      if (!resp.ok || parsed?.error) { setError(parsed?.error || `Server error (${resp.status}). Please try again.`); return; }
      if (!parsed?.client_secret) { setError("No card form returned — please try again."); return; }
      setSetupClientSecret(parsed.client_secret);
      setAddingCard(true);
    } catch (err) {
      setError(err?.name === "AbortError"
        ? "Connection timed out loading the card form. Check your internet and tap “Add Credit Card” again."
        : (err?.message || "Could not start card form"));
    } finally {
      setStripeLoading(false);
    }
  };

  const selectedClient = pickedClient;

  // ★ Enrollment charges a real card — show the outcome before closing.
  if (enrollResult) {
    const ok = enrollResult.ok;
    return (
      <div onClick={()=>{setEnrollResult(null); onEnrolled();}} style={{position:"fixed",inset:0,background:"rgba(15,15,25,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
        <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:420,width:"100%",padding:26,textAlign:"center",boxShadow:"0 24px 70px rgba(0,0,0,0.4)",border:`3px solid ${ok?C.green:"#cc6600"}`}}>
          <div style={{fontSize:46,lineHeight:1,marginBottom:10}}>{ok?"💳✅":"⚠️"}</div>
          <div style={{fontSize:19,fontWeight:800,color:C.text,fontFamily:S,marginBottom:8}}>{enrollResult.title}</div>
          <div style={{fontSize:13.5,color:"#55524a",lineHeight:1.6}}>{enrollResult.body}</div>
          {enrollResult.benefits?.length > 0 && (
            <div style={{marginTop:14,padding:"12px 14px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:9,textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Added to her wallet</div>
              {enrollResult.benefits.map((b,i)=>(
                <div key={i} style={{fontSize:12.5,color:C.text,padding:"2px 0"}}>💳 {b}</div>
              ))}
              <div style={{fontSize:10.5,color:C.muted,marginTop:6}}>Redeem at checkout · tops up automatically every month</div>
            </div>
          )}
          {selectedClient?.name && <div style={{fontSize:12,color:C.muted,marginTop:8}}>{selectedClient.name}</div>}
          <button onClick={()=>{setEnrollResult(null); onEnrolled();}} style={{marginTop:18,width:"100%",padding:"12px",fontSize:14,fontWeight:700,background:ok?`linear-gradient(135deg,${C.green},#246b45)`:"linear-gradient(135deg,#cc6600,#a35200)",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",fontFamily:F}}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:14,maxWidth:520,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
          <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S}}>Enroll Client in Membership</div>
        </div>

        <div style={{padding:22}}>
          <Field label="Client *">
            {selectedClient ? (
              <div style={{padding:"10px 12px",background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:7,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{selectedClient.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{selectedClient.email}</div>
                </div>
                <button onClick={()=>{setClientId(""); setSearch(""); setPickedClient(null);}} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:18}}>×</button>
              </div>
            ) : (
              <>
                <input autoFocus value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search by name, email, or phone..." style={inp}/>
                {search.trim().length >= 2 && (
                  <div style={{maxHeight:200,overflow:"auto",border:"1px solid "+C.border,borderRadius:7,marginTop:6}}>
                    {results.map(c => {
                      const pick = ()=>{setClientId(c.id); setPickedClient(c); setSearch(""); setResults([]);};
                      return (
                        <div key={c.id}
                          onMouseDown={(e)=>{e.preventDefault(); pick();}}
                          onTouchEnd={(e)=>{e.preventDefault(); pick();}}
                          style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid "+C.border,fontSize:12}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
                          onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                          <div style={{fontWeight:600,color:C.text}}>{c.name}</div>
                          <div style={{fontSize:10,color:C.muted}}>{c.email || c.phone || ""}</div>
                        </div>
                      );
                    })}
                    {searching && <div style={{padding:14,textAlign:"center",fontSize:11,color:C.muted}}>Searching…</div>}
                    {!searching && results.length === 0 && <div style={{padding:14,textAlign:"center",fontSize:11,color:C.muted}}>No matches</div>}
                  </div>
                )}
              </>
            )}
          </Field>

          <Field label="Plan *">
            <select value={planId} onChange={e=>setPlanId(e.target.value)} style={inp}>
              <option value="">Choose a plan...</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} — ${(+p.price).toFixed(0)}/{p.billing_interval}
                </option>
              ))}
            </select>
            {plans.length === 0 && <div style={{fontSize:11,color:"#cc6600",marginTop:5}}>⚠ No active plans. Create one in the Plans tab first.</div>}
          </Field>

          {/* ★ Made an explicit choice 2026-09-02: this used to be an optional
              "billing day" box. Filling it silently starts a TRIAL and skips
              today's charge — staff read it as "which day to bill monthly" and
              ended up with an unpaid member (Maria Ellis-Morton). Now the two
              outcomes are stated up front and charging today is the default. */}
          <Field label="Billing">
            <label style={{display:"flex",gap:10,alignItems:"flex-start",padding:"11px 13px",borderRadius:9,cursor:"pointer",
              background: billingDay ? "#f7f5f0" : "rgba(46,139,87,0.10)",
              border: "1px solid " + (billingDay ? "#e8e4df" : "rgba(46,139,87,0.45)")}}>
              <input type="radio" checked={!billingDay} onChange={()=>setBillingDay("")} style={{marginTop:3,accentColor:"#2e8b57"}}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>New member — charge now</div>
                <div style={{fontSize:11,color:"#8a8276",marginTop:2}}>Charges the full month today, then automatically on this same date every month.</div>
              </div>
            </label>
            <label style={{display:"flex",gap:10,alignItems:"flex-start",padding:"11px 13px",borderRadius:9,cursor:"pointer",marginTop:8,
              background: billingDay ? "rgba(217,119,6,0.10)" : "#f7f5f0",
              border: "1px solid " + (billingDay ? "rgba(217,119,6,0.45)" : "#e8e4df")}}>
              <input type="radio" checked={!!billingDay} onChange={()=>setBillingDay(String(new Date().getDate()))} style={{marginTop:3,accentColor:"#cc6600"}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>Existing member moving over — don't charge today</div>
                <div style={{fontSize:11,color:"#8a8276",marginTop:2}}>Keeps their current billing date so they aren't double-charged.</div>
                {!!billingDay && (
                  <div style={{marginTop:8}}>
                    <input type="number" min="1" max="31" value={billingDay} onChange={e=>setBillingDay(e.target.value.replace(/[^0-9]/g,"").slice(0,2))} placeholder="Day of month, e.g. 15" style={{...inp,maxWidth:200}}/>
                    <div style={{fontSize:11,color:"#92400e",marginTop:5,fontWeight:600}}>
                      ⚠ No charge today — first charge lands on day {+billingDay || "?"} of the month, then monthly.
                    </div>
                  </div>
                )}
              </div>
            </label>
          </Field>

          {clientId && (
            <Field label="Payment Method">
              {savedCards.length > 0 && !addingCard ? (
                <>
                  <select value={paymentMethodId} onChange={e=>setPaymentMethodId(e.target.value)} style={inp}>
                    {savedCards.map(c => (
                      <option key={c.stripe_payment_method_id} value={c.stripe_payment_method_id}>
                        {c.card_brand} •••• {c.card_last4} {c.is_default ? "(default)" : ""}
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={stripeLoading} onClick={startAddCard} style={{marginTop:6,padding:"7px 12px",background:"transparent",color:C.gold,border:"1px dashed "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:700,cursor:stripeLoading?"not-allowed":"pointer",fontFamily:F,opacity:stripeLoading?0.6:1}}>
                    {stripeLoading ? "Opening…" : "+ Add another card"}
                  </button>
                </>
              ) : addingCard && setupClientSecret ? (
                <div style={{padding:"14px",background:C.surface,border:"2px solid "+C.borderGold,borderRadius:9}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.gold,marginBottom:10}}>💳 Enter Card to Save & Charge Monthly</div>
                  {stripeReady === false ? (
                    <div style={{fontSize:12,color:C.red,lineHeight:1.5}}>
                      ⚠ The secure card form couldn't load — this is almost always a weak/dropped internet connection.
                      Connect to stronger Wi-Fi, refresh the page, and try again.
                    </div>
                  ) : (
                    <Elements stripe={stripePromise} options={{clientSecret: setupClientSecret, locale:"en", appearance:{theme:"stripe"}}}>
                      <SaveCardForm clientId={+clientId} onSaved={(pm) => {
                        // pm is the saved row {id, stripe_payment_method_id, card_brand, card_last4}
                        setSavedCards(prev => [...prev, pm]);
                        setPaymentMethodId(pm.stripe_payment_method_id);
                        setAddingCard(false);
                        setSetupClientSecret("");
                      }} onCancel={() => { setAddingCard(false); setSetupClientSecret(""); }} onError={setError}/>
                    </Elements>
                  )}
                </div>
              ) : (
                <div style={{padding:"14px",background:"#fffaf0",border:"1px solid rgba(255,165,0,0.4)",borderRadius:9}}>
                  <div style={{fontSize:12,color:"#9a3412",fontWeight:600,marginBottom:8}}>⚠ No card on file. Add one now to activate the subscription — it will be charged monthly on this exact date.</div>
                  <button type="button" disabled={stripeLoading} onClick={startAddCard} style={{padding:"9px 16px",background:C.gold,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:stripeLoading?"not-allowed":"pointer",fontFamily:F,opacity:stripeLoading?0.6:1}}>
                    {stripeLoading ? "Opening…" : "💳 Add Credit Card"}
                  </button>
                </div>
              )}
            </Field>
          )}

          {error && <div style={{padding:"10px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:12,marginTop:10}}>❌ {error}</div>}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16,paddingTop:14,borderTop:"1px solid "+C.border}}>
            <button onClick={onClose} disabled={saving} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            {(() => {
              // Disabled until a client + plan + card-on-file are all picked.
              // We want every membership to start with a chargeable card so
              // the recurring monthly run never fails on day 1.
              const needsCard = clientId && savedCards.length === 0 && !paymentMethodId;
              const disabled = saving || !clientId || !planId || needsCard;
              const label = saving
                ? "Enrolling…"
                : needsCard
                ? "Add Card to Enroll"
                : billingDay
                ? `Enroll · 1st charge day ${+billingDay}`
                : "Enroll & Charge";
              return (
                <button onClick={enroll} disabled={disabled} title={needsCard ? "Add a card on file first — every membership needs a payment method to charge monthly" : ""}
                  style={{padding:"9px 22px",background: disabled ? C.light : "linear-gradient(135deg,"+C.gold+","+C.goldD+")",color: disabled ? C.muted : "#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor: disabled ? "not-allowed" : "pointer",fontFamily:F,opacity: disabled ? 0.65 : 1}}>
                  {label}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CHARGES TAB
// ════════════════════════════════════════════════════════════
function ChargesTab() {
  const [charges, setCharges] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    let q = supabase.from("membership_charges")
      .select("*, clients(name, email), client_memberships(membership_plans(name))")
      .order("charged_at", { ascending: false })
      .limit(200);
    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    const { data } = await q;
    setCharges(data || []);
    setLoading(false);
  };

  const total = charges.filter(c => c.status === "succeeded").reduce((s, c) => s + +c.amount, 0);
  const failed = charges.filter(c => c.status === "failed").length;

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:16}}>
        <Stat label="Total Collected" value={`$${total.toFixed(2)}`} color={C.green}/>
        <Stat label="Failed Charges" value={failed} color={C.red}/>
        <Stat label="Last 200 Charges" value={charges.length} color={C.gold}/>
      </div>

      <div style={{display:"flex",gap:10,marginBottom:14}}>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{...inp, width:"auto"}}>
          <option value="all">All charges</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {loading ? (
        <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>
      ) : charges.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
          <div style={{fontSize:36,marginBottom:8}}>💳</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text}}>No charges yet</div>
        </div>
      ) : (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
          {charges.map(c => (
            <div key={c.id} style={{padding:"12px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",gap:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{c.clients?.name || "—"}</div>
                <div style={{fontSize:11,color:C.muted}}>{c.client_memberships?.membership_plans?.name || ""}</div>
              </div>
              <div style={{fontSize:14,fontWeight:700,color:c.status==="succeeded"?C.green:c.status==="failed"?C.red:C.muted}}>
                ${(+c.amount).toFixed(2)}
              </div>
              <StatusBadge status={c.status === "succeeded" ? "active" : c.status === "failed" ? "cancelled" : "incomplete"} />
              <div style={{fontSize:11,color:C.muted,minWidth:100,textAlign:"right"}}>
                {new Date(c.charged_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{padding:14,background:C.card,border:"1px solid "+C.border,borderRadius:10,borderLeft:"4px solid "+color}}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{marginBottom:14}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5,fontFamily:F}}>{label}</label>
      {children}
    </div>
  );
}
